import React, { useEffect, useState } from 'react';

import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { useTranslation } from '../../../../../i18n';

import { createStripeIntent } from '../../../../../client';

import { useSenderProvider } from '../../../../../entities/providers/hooks';

import { Alert } from '../../../../element';

import { PayWithStripe } from '../../payment-method.types';

import { cardOptions } from './stripe-card-config';
import { CardPaySystemLogo, StyledStripeCard, StyledStripeSubmit } from './styled';

export const StripeCard: React.FC<PayWithStripe> = ({ amount, currency, onError, onPay }) => {
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [clientSecret, setClientSecret] = useState('');

  const [t] = useTranslation('rates');

  const stripe = useStripe();
  const elements = useElements();

  const provider = useSenderProvider();

  useEffect(() => {
    // Create PaymentIntent as soon as the page loads
    createStripeIntent({
      currency,
      amount,
      provider,
    }).then((data) => {
      setClientSecret(data.client_secret);
    });
  }, []);

  const handleChange = async (event: NotTyped) => {
    // Listen for changes in the CardElement
    // and display any errors as the customer types their card details
    setDisabled(event.empty);
    setError(event.error ? event.error.message : '');
  };

  const handleSubmit = async (ev: NotTyped) => {
    if (!stripe || !elements) {
      return;
    }

    ev.preventDefault();
    setProcessing(true);

    const payload = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
      },
    } as NotTyped);

    if (payload.error) {
      const {
        error: { message },
      } = payload;

      if (typeof onError === 'function') {
        onError();
      }

      setError(`Payment failed ${message}`);
      setProcessing(false);
    } else {
      setError(null);
      setProcessing(false);
      setSucceeded(true);

      if (typeof onPay === 'function') {
        onPay(payload.paymentIntent);
      }
    }
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <div className="mb-20">
        <CardPaySystemLogo>
          <img src="/static/img/pay/visa.svg" alt="visa" />
          <img src="/static/img/pay/mastercard.svg" alt="visa" />
          <img src="/static/img/pay/maestrocard.svg" alt="visa" />
        </CardPaySystemLogo>

        <StyledStripeCard>
          <CardElement id="card-element" options={cardOptions} onChange={handleChange} />
        </StyledStripeCard>
      </div>

      {error && <Alert type="error" showIcon title={error} closable className="is-light mb-20" />}

      <div className="text-center">
        <StyledStripeSubmit type="submit" disabled={Boolean(processing || disabled || succeeded)}>
          {processing ? '...' : t('rates.payments.pay_now_btn')}
        </StyledStripeSubmit>
      </div>
    </form>
  );
};
